import { useCallback, useEffect, useRef, useState } from 'react';
import { useMappingProjectSessionStore } from './useMappingProjectSessionStore';
import {
  buildSettingInfoProfileKey,
  getProfileKeyParts,
} from '../lib/mappingPageProjectProfileUtils';
import { traceMappingFlow } from '../lib/mappingTrace';
import { requestGuardedProfileLoad } from '../lib/guardedProfileNavigation';
import { normalizeMappingProjectKey } from '../lib/mappingProfileProjectTrust';
import type {
  ProjectSessionProfileConfirmationSource,
  UseProjectProfileGateArgs,
  UseProjectProfileGateResult,
} from './projectProfileGateContracts';
import { useProjectProfileGateEvaluation } from './useProjectProfileGateEvaluation';

export type {
  ProjectProfileDocumentInfo,
  ProjectProfilePreferredProfile,
  UseProjectProfileGateArgs,
  UseProjectProfileGateResult,
} from './projectProfileGateContracts';

export function useProjectProfileGate(args: UseProjectProfileGateArgs): UseProjectProfileGateResult {
  const {
    activeUserId,
    activeDocumentInfo,
    selectedProfileKey,
    selectedSettingsType,
    lastAppliedProfileKey,
    profileApplyProgress,
    profiles,
    profilesLoading,
    projectPreferredProfile,
    projectIdentityPending,
    dockablePaneVisible,
    isLiveWizardMappingHandoff,
    profileTrustRevision,
    onSelectedProfileChange,
    onLoadProfile,
  } = args;
  const projectSessionProfileConfirmations = useMappingProjectSessionStore(
    (state) => state.confirmations
  );
  const manualProjectProfileSelection = useMappingProjectSessionStore(
    (state) => state.manualSelection
  );
  const confirmProfileForProject = useMappingProjectSessionStore(
    (state) => state.confirmProfileForProject
  );
  const setManualSelection = useMappingProjectSessionStore(
    (state) => state.setManualSelection
  );
  const [projectProfileGateProfileKey, setProjectProfileGateProfileKey] = useState('');
  const projectProfileGateProjectRef = useRef<string | null>(null);
  const activeProjectSessionKey = normalizeMappingProjectKey(activeDocumentInfo.projectKey);
  const projectSessionProfileConfirmation = activeProjectSessionKey
    ? projectSessionProfileConfirmations[activeProjectSessionKey] ?? null
    : null;
  const projectSessionConfirmedProfileKey = projectSessionProfileConfirmation?.profileKey ?? null;
  const projectSessionProfileConfirmationSource = projectSessionProfileConfirmation?.source ?? null;
  const evaluation = useProjectProfileGateEvaluation({
    activeUserId,
    activeDocumentInfo,
    selectedProfileKey,
    selectedSettingsType,
    lastAppliedProfileKey,
    profileApplyProgress,
    profiles,
    profilesLoading,
    projectPreferredProfile,
    projectIdentityPending,
    dockablePaneVisible,
    isLiveWizardMappingHandoff,
    profileTrustRevision,
    projectSessionConfirmedProfileKey,
    projectSessionProfileConfirmationSource,
    manualProjectProfileSelection,
  });
  const {
    profileApplySessionActive,
    projectProfileGateDefaultProfileKey,
    ...publicEvaluation
  } = evaluation;

  useEffect(() => {
    const projectChanged = projectProfileGateProjectRef.current !== evaluation.activeProjectSessionKey;
    projectProfileGateProjectRef.current = evaluation.activeProjectSessionKey;

    setProjectProfileGateProfileKey((currentProfileKey) => {
      if (projectChanged) {
        return evaluation.projectProfileGateOpen
          ? projectProfileGateDefaultProfileKey
          : '';
      }

      const hasCurrentProfile =
        currentProfileKey !== '' &&
        profiles.some((profile) => buildSettingInfoProfileKey(profile) === currentProfileKey);
      return hasCurrentProfile ? currentProfileKey : projectProfileGateDefaultProfileKey;
    });
  }, [
    evaluation.activeProjectSessionKey,
    evaluation.projectProfileGateOpen,
    projectProfileGateDefaultProfileKey,
    profiles,
  ]);

  const confirmProjectSessionProfile = useCallback(
    (profileKey: string, source: ProjectSessionProfileConfirmationSource = 'manual') => {
      const projectSessionKey = evaluation.activeProjectSessionKey;
      if (!projectSessionKey || !profileKey) {
        return;
      }

      confirmProfileForProject(projectSessionKey, profileKey, source);
      traceMappingFlow('project profile session confirmed', {
        projectKey: activeDocumentInfo.projectKey,
        documentName: activeDocumentInfo.documentName,
        profileKey,
        source,
      });
    },
    [
      activeDocumentInfo.documentName,
      activeDocumentInfo.projectKey,
      confirmProfileForProject,
      evaluation.activeProjectSessionKey,
    ]
  );
  const handleLoadProfileForCurrentProject = useCallback(
    async (
      profileKeyOverride?: string,
      confirmationSource: ProjectSessionProfileConfirmationSource = 'manual'
    ) => {
      const profileKeyToLoad =
        typeof profileKeyOverride === 'string' && profileKeyOverride.trim()
          ? profileKeyOverride
          : selectedProfileKey;
      const loaded = await onLoadProfile(profileKeyToLoad);
      if (loaded) {
        confirmProjectSessionProfile(profileKeyToLoad, confirmationSource);
        setManualSelection((current) =>
          current?.projectKey === evaluation.activeProjectSessionKey &&
          current.profileKey === profileKeyToLoad
            ? null
            : current
        );
      }

      return loaded;
    },
    [
      confirmProjectSessionProfile,
      evaluation.activeProjectSessionKey,
      onLoadProfile,
      selectedProfileKey,
      setManualSelection,
    ]
  );
  const handleWorkspaceSelectedProfileChange = useCallback(
    (profileKey: string) => {
      if (
        !evaluation.activeProjectSessionKey ||
        !profileKey ||
        profileKey === evaluation.projectProfileAutoPassProfileKey
      ) {
        setManualSelection(null);
      } else if (profileKey !== selectedProfileKey) {
        setManualSelection({
          projectKey: evaluation.activeProjectSessionKey,
          profileKey,
        });
        traceMappingFlow('project profile auto-pass paused for manual selection', {
          projectKey: activeDocumentInfo.projectKey,
          documentName: activeDocumentInfo.documentName,
          selectedProfileKey: profileKey,
          projectProfileAutoPassProfileKey: evaluation.projectProfileAutoPassProfileKey,
        });
      }

      onSelectedProfileChange(profileKey);
    },
    [
      activeDocumentInfo.documentName,
      activeDocumentInfo.projectKey,
      evaluation.activeProjectSessionKey,
      evaluation.projectProfileAutoPassProfileKey,
      onSelectedProfileChange,
      selectedProfileKey,
      setManualSelection,
    ]
  );
  const handleProjectProfileGateSelectedProfileKeyChange = useCallback((profileKey: string) => {
    setProjectProfileGateProfileKey(profileKey);
  }, []);
  const handleProjectProfileGateConfirm = useCallback(async (): Promise<boolean> => {
    if (!projectProfileGateProfileKey) {
      return false;
    }

    const profileKeyParts = getProfileKeyParts(projectProfileGateProfileKey);
    traceMappingFlow('project profile gate confirm', {
      projectKey: activeDocumentInfo.projectKey,
      documentName: activeDocumentInfo.documentName,
      profileKey: projectProfileGateProfileKey,
      settingsType: profileKeyParts.settingsType,
    });
    // The gate is a profile destination like any other control, so its load runs
    // inside the same guard; the dialog itself is this destination's consent.
    return requestGuardedProfileLoad(projectProfileGateProfileKey, 'profileLoad', () =>
      handleLoadProfileForCurrentProject(projectProfileGateProfileKey)
    );
  }, [
    activeDocumentInfo.documentName,
    activeDocumentInfo.projectKey,
    handleLoadProfileForCurrentProject,
    projectProfileGateProfileKey,
  ]);

  const projectProfileAutoPassLoadedKey: string | null = null;
  const trustedProjectProfileAwaitingSessionLoad = false;
  const projectProfileChoiceBlocksQuickFill =
    evaluation.projectProfileGateOpen ||
    (evaluation.projectProfileGateEvaluationNeeded &&
      !evaluation.profileConfirmedForCurrentSession &&
      lastAppliedProfileKey !== selectedProfileKey);
  const quickFillProfileRestorePending =
    Boolean(evaluation.activeProjectSessionKey) &&
    (trustedProjectProfileAwaitingSessionLoad || projectProfileChoiceBlocksQuickFill);

  return {
    ...publicEvaluation,
    projectSessionConfirmedProfileKey,
    projectSessionProfileConfirmationSource,
    projectProfileAutoPassLoadedKey,
    trustedProjectProfileAwaitingSessionLoad,
    projectProfileChoiceBlocksQuickFill,
    quickFillProfileRestorePending,
    projectProfileGateProfileKey,
    handleWorkspaceSelectedProfileChange,
    handleLoadProfileForCurrentProject,
    handleProjectProfileGateSelectedProfileKeyChange,
    handleProjectProfileGateConfirm,
  };
}
